import React from 'react';
import { Table } from './Table';
import { TableWithSelectionProps, SelectionTableColumn } from './SelectionTableTypes';
import classNames from 'classnames';

/**
 * Enhanced table component with row selection capabilities via checkboxes
 * 
 * @example
 * ```tsx
 * <TableWithSelection
 *   data={donations}
 *   columns={columns}
 *   selectedIds={selectedIds}
 *   onSelectionChange={handleSelectItem}
 *   onSelectAll={handleSelectAll}
 *   allSelected={allSelected}
 *   onRowClick={handleRowClick}
 * />
 * ```
 */
export const TableWithSelection = <T extends {}>({
  data = [],
  columns,
  selectedIds,
  onSelectionChange,
  onSelectAll,
  allSelected = false,
  getRowKey,
  headerComponent,
  ...restProps
}: TableWithSelectionProps<T>) => {
  // Generate row key if not provided
  const generateRowKey = (row: T, index: number): string | number => {
    if (getRowKey) {
      return getRowKey(row);
    }
    
    if ((row as any).id) {
      return (row as any).id;
    }
    
    if ((row as any)._id) {
      return (row as any)._id;
    }
    
    return index;
  };

  // Add selection column
  const selectionColumns: SelectionTableColumn<T>[] = [
    {
      id: 'selection',
      label: '',
      isSelectionColumn: true,
      width: '40px',
      priority: 'high',
      format: (_, row, rowIndex) => {
        // Ensure rowIndex is a number (required for generateRowKey)
        const index = typeof rowIndex === 'number' ? rowIndex : 0;
        const rowKey = generateRowKey(row, index);
        const isSelected = selectedIds.has(rowKey);
        
        return (
          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelectionChange(row, e.target.checked)}
              className="form-checkbox h-5 w-5 text-primary-100 rounded focus:ring-primary-70"
              aria-label={`Select row ${index + 1}`}
            />
          </div>
        );
      },
    },
    ...columns,
  ];

  // Customize table header to add selection checkbox
  const CustomHeader = () => (
    <thead className={classNames('sticky top-0 z-10 bg-primary-50/85 dark:bg-slate-800/90')}>
      <tr>
        <th className="w-10 px-4 py-3 text-left bg-primary-50/85 dark:bg-slate-800/90 border-b border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
              className="form-checkbox h-5 w-5 text-primary-100 rounded focus:ring-primary-70"
              aria-label="Select all rows"
            />
          </div>
        </th>
        {columns.map((column) => (
          <th 
            key={column.id}
            className={classNames(
              'px-4 py-3 bg-primary-50/85 dark:bg-slate-800/90 text-left text-sm font-semibold tracking-wide text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-white/10',
              column.sortable && 'cursor-pointer',
              column.className
            )}
            style={{ width: column.width }}
            onClick={() => {
              if (column.sortable && restProps.onSortChange) {
                const direction = 
                  restProps.sortBy === column.id && restProps.sortDirection === 'asc' 
                    ? 'desc' 
                    : 'asc';
                restProps.onSortChange(column.id, direction);
              }
            }}
          >
            {column.label}
            {column.sortable && restProps.sortBy === column.id && (
              <span className="ml-1 text-xs">
                {restProps.sortDirection === 'asc' ? '▲' : '▼'}
              </span>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );

  return (
    <Table
      data={data}
      columns={selectionColumns}
      {...restProps}
      headerComponent={headerComponent || <CustomHeader />}
    />
  );
};

export default TableWithSelection;